import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wrench, Euro, AlertCircle } from 'lucide-react';
import { useConfigurator } from '../ConfiguratorProvider';
import { mountingSystems } from '../productData';

export const MountingSystemSelection = () => {
  const { configuration, setConfiguration } = useConfigurator();

  const handleMountingSystemSelect = (mountingSystem: typeof mountingSystems[0]) => {
    setConfiguration({ mountingSystem });
  };

  const availableMountingSystems = mountingSystems.filter(system => 
    configuration.roofType && system.roofTypes.includes(configuration.roofType.id)
  );

  if (!configuration.roofType) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Selecteer eerst een daktype</h3>
        <p className="text-muted-foreground">
          Ga terug naar de vorige stap om je daktype te selecteren
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">Kies je montagesysteem</h3>
        <p className="text-muted-foreground">
          Voor {configuration.roofType.name.toLowerCase()} hebben we de volgende opties
        </p>
      </div>

      <div className="grid gap-4">
        {availableMountingSystems.map((mountingSystem) => (
          <Card 
            key={mountingSystem.id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              configuration.mountingSystem?.id === mountingSystem.id 
                ? 'ring-2 ring-primary bg-primary/5' 
                : ''
            }`}
            onClick={() => handleMountingSystemSelect(mountingSystem)}
          >
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg flex items-center gap-3">
                  <Wrench className="h-5 w-5 text-primary" />
                  {mountingSystem.name}
                </CardTitle>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Euro className="h-3 w-3" />
                  €{mountingSystem.price}/paneel
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-sm leading-relaxed mb-4">
                {mountingSystem.description}
              </CardDescription>
              
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">
                  Totaalprijs montage ({configuration.panelCount} panelen):
                </div>
                <div className="font-bold text-lg">
                  €{(mountingSystem.price * configuration.panelCount).toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">
                  Inclusief bevestigingsmateriaal en arbeid
                </div>
              </div>
              
              {configuration.mountingSystem?.id === mountingSystem.id && (
                <Button className="w-full mt-4" variant="secondary">
                  Geselecteerd ✓
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {configuration.mountingSystem && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4 text-primary" />
              <span className="font-medium">Jouw selectie:</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {configuration.mountingSystem.name} voor €{(configuration.mountingSystem.price * configuration.panelCount).toLocaleString()} 
              ({configuration.panelCount} panelen)
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};